import { useCallback, useEffect, useState } from 'react'

/**
 * Chart palette for App Analytics V2.
 *
 * Chart CHROME (grid, axis text, surface) is read from CoUI's own `--cui-*`
 * tokens at runtime, so light/dark mode keeps working without hard-coded hex —
 * the house rule in README "Page shell". Chart.js paints onto a canvas and
 * cannot resolve CSS variables itself, so they are resolved here.
 *
 * Chart SERIES colours are the one deliberate exception: CoreUI has no
 * categorical data-viz ramp, and these eight are a set validated for
 * colour-blind separation (iPhone/Android clear CVD delta-E 24.7 light /
 * 26.8 dark). Picking `--cui-primary`/`--cui-success`/... instead would put
 * unvalidated, near-identical hues next to each other. Both modes are stepped
 * for their own surface rather than being an automatic flip.
 */
const SERIES = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'],
}

/** iOS keeps slot 1, Android slot 2, always — colour follows the platform, never its rank. */
export const PLATFORM_META = [
  { key: 'ios', label: 'iPhone (iOS)', slot: 0 },
  { key: 'android', label: 'Android', slot: 1 },
]

const readToken = (name, fallback) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name)
  return value && value.trim() ? value.trim() : fallback
}

const detectMode = () => {
  if (typeof document === 'undefined') return 'light'
  const attr = document.documentElement.getAttribute('data-coreui-theme')
  if (attr === 'dark' || attr === 'light') return attr
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

const buildTheme = () => {
  const mode = detectMode()
  const isDark = mode === 'dark'

  return {
    mode,
    surface: readToken('--cui-card-bg', isDark ? '#2a303c' : '#ffffff'),
    grid: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 21, 0.1)',
    text: readToken('--cui-body-color', isDark ? '#c8ccd4' : '#4f5d73'),
    textMuted: readToken('--cui-secondary-color', isDark ? '#9da5b1' : '#768192'),
    series: SERIES[mode],
    neutral: isDark ? '#6c7581' : '#a9b3c1',
  }
}

/**
 * Recomputes when CoreUI's theme attribute flips or the OS preference changes,
 * so charts repaint rather than keeping stale colours.
 */
export const useChartTheme = () => {
  const [theme, setTheme] = useState(buildTheme)

  const refresh = useCallback(() => setTheme(buildTheme()), [])

  useEffect(() => {
    const observer = new MutationObserver(refresh)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-coreui-theme'],
    })

    const media = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null
    if (media && media.addEventListener) media.addEventListener('change', refresh)

    return () => {
      observer.disconnect()
      if (media && media.removeEventListener) media.removeEventListener('change', refresh)
    }
  }, [refresh])

  return theme
}

/**
 * Shared Chart.js options. `maintainAspectRatio: false` means the chart fills
 * its container, so every chart must sit in an element with a real height —
 * see the .av2-chart-* classes in AppAnalytics.css.
 *
 * IMPORTANT: every <CChart> here passes `customTooltips={false}`.
 *
 * CoreUI's CChart defaults that prop to TRUE, which swaps Chart.js's canvas
 * tooltip for a DOM node positioned at `canvas.offsetLeft + caretX` with no
 * clamping to the container or the viewport. That caused two real bugs:
 * the tooltip was clipped at the card's edge, and once it pushed past the
 * document width a horizontal scrollbar appeared, which reflowed the page,
 * which moved the cursor's target, which toggled the scrollbar again — the
 * page visibly shook while hovering a chart.
 *
 * It also meant the `tooltip` styling below was silently ignored, because the
 * DOM tooltip is built from CoreUI's own markup. With customTooltips off, the
 * tooltip is painted inside the canvas (so it can never affect page layout)
 * and these options actually apply.
 */
export const baseChartOptions = (theme, overrides = {}) => ({
  maintainAspectRatio: false,
  responsive: true,
  resizeDelay: 100,
  interaction: { mode: 'index', intersect: false },
  ...overrides,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        color: theme.text,
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 8,
        padding: 16,
      },
    },
    tooltip: {
      backgroundColor: theme.surface,
      titleColor: theme.text,
      bodyColor: theme.text,
      borderColor: theme.grid,
      borderWidth: 1,
      padding: 10,
      displayColors: true,
    },
    ...(overrides.plugins || {}),
  },
})

/** Recessive axis styling. */
export const axis = (theme, extra = {}) => ({
  grid: { color: theme.grid, drawBorder: false, lineWidth: 1 },
  ...extra,
  // Merged last so a caller overriding `ticks` cannot accidentally drop the
  // text colour and leave the axis labels at Chart.js's default near-black.
  ticks: { color: theme.textMuted, ...(extra.ticks || {}) },
})
